import { Menu, MenuItem } from "@mui/material";
import LightPlusBtn from "components/shared/LightPlus";
import { CalculationMethod } from "containers/Channels";
import type { IChannel } from "containers/Channels/types";
import type { ChannelData, ChannelParams, ITermFormData } from "containers/TermFormContainer";
import { useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import type { CalculationBaseColumn, ICalculationBaseColumnLabel } from "store/calculationBaseColumnLabelsStore";

import { formatTypesForOverridePanel, type FormatTypeOption } from "../../utils";
import ChannelBaseColumnsFields, { channelUsesSalesBaseColumns } from "../ChannelBaseColumnsFields";
import FormatOverrideHeader from "../FormatOverrideHeader";
import GroupMilestonesInput from "../GroupMilestonesInput";
import { resolveChannelMilestoneBaseColumn } from "../milestoneBaseColumnUtils";
import type { GroupWithChannels } from "../types";

type GroupFormatOverridesProps = {
    group: GroupWithChannels;
    formatTypesOnWork: FormatTypeOption[];
    chosenChannels: ChannelParams[];
    calculationBaseColumnLabels: ICalculationBaseColumnLabel[];
    hasMilestones: boolean;
    useFormData: Pick<ITermFormData, "setError" | "clearErrors">;
    handleToggleGroupFormatTypeOverride: (formatTypeId: number, enabled: boolean) => void;
    handleGroupCalculationBaseColumnChange: (
        groupId: number,
        calculationBaseColumn: CalculationBaseColumn,
        formatTypeId?: number | null,
    ) => void;
    handleGroupMilestoneColumnChange: (
        groupId: number,
        milestoneBaseColumn: CalculationBaseColumn,
        formatTypeId?: number | null,
    ) => void;
    handleGroupUpdate: (
        newGroupData: Record<string, unknown>,
        groupChannels: IChannel[],
        formatTypeId?: number | null,
    ) => void;
};

const GroupFormatOverrides = ({
    group,
    formatTypesOnWork,
    chosenChannels,
    calculationBaseColumnLabels,
    hasMilestones,
    useFormData,
    handleToggleGroupFormatTypeOverride,
    handleGroupCalculationBaseColumnChange,
    handleGroupMilestoneColumnChange,
    handleGroupUpdate,
}: GroupFormatOverridesProps) => {
    const [t] = useTranslation();
    const addButtonRef = useRef<HTMLSpanElement>(null);
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const referenceChannel = useMemo(
        () => group.channels.find(channelUsesSalesBaseColumns) ?? group.channels[0],
        [group.channels],
    );
    const overrideByFormatId = useMemo(() => {
        const byId = new Map<number, ChannelParams>();
        const groupChannelIds = new Set(group.channels.map((channel) => channel.id));
        for (const row of chosenChannels) {
            if (!groupChannelIds.has(row.channelId) || row.formatTypeId == null) continue;
            if (!byId.has(row.formatTypeId)) byId.set(row.formatTypeId, row);
        }
        return byId;
    }, [chosenChannels, group.channels]);
    const { remaining: remainingFormatTypes, active: activeFormatTypes } = formatTypesForOverridePanel(
        formatTypesOnWork,
        overrideByFormatId.keys(),
    );

    const addFormatOverride = (formatTypeId: number) => {
        handleToggleGroupFormatTypeOverride(formatTypeId, true);
        setIsMenuOpen(false);
    };

    const handleAddClick = () => {
        if (remainingFormatTypes.length === 1) {
            addFormatOverride(remainingFormatTypes[0].id);
            return;
        }
        setIsMenuOpen(true);
    };

    const hasExistingOverrides = overrideByFormatId.size > 0;
    const canCustomizeByFormat =
        !!referenceChannel &&
        referenceChannel.calculationMethod === CalculationMethod.PROPORTIONAL &&
        (formatTypesOnWork.length > 0 || hasExistingOverrides);

    if (!canCustomizeByFormat) return null;

    return (
        <div className="channel-format-overrides">
            <div className="channel-format-overrides__intro">
                <div className="channel-format-overrides__header">
                    <span>{t("ConnectTitle.CustomizeByFormatType")}</span>
                    {remainingFormatTypes.length > 0 && (
                        <span ref={addButtonRef} className="channel-format-overrides__add">
                            <LightPlusBtn
                                type="plus"
                                tooltipTitle={t("ConnectTitle.AddFormatOverride")}
                                onClick={handleAddClick}
                            />
                        </span>
                    )}
                    <Menu
                        className="channel-format-overrides-menu"
                        anchorEl={addButtonRef.current}
                        open={isMenuOpen}
                        onClose={() => setIsMenuOpen(false)}
                        elevation={0}
                        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                        transformOrigin={{ vertical: "top", horizontal: "left" }}
                    >
                        {remainingFormatTypes.map((formatType) => (
                            <MenuItem key={formatType.id} onClick={() => addFormatOverride(formatType.id)}>
                                {formatType.name}
                            </MenuItem>
                        ))}
                    </Menu>
                </div>
                <p className="channel-format-overrides__hint">{t("ConnectTitle.CustomizeByFormatTypeHint")}</p>
            </div>
            {activeFormatTypes.map((formatType) => {
                const override = overrideByFormatId.get(formatType.id);
                if (!override || !referenceChannel) return null;
                return (
                    <div className="channel-format-override" key={formatType.id}>
                        <FormatOverrideHeader
                            name={formatType.name}
                            onRemove={() => handleToggleGroupFormatTypeOverride(formatType.id, false)}
                        />
                        <ChannelBaseColumnsFields
                            channel={referenceChannel}
                            existingChannel={override}
                            calculationBaseColumnLabels={calculationBaseColumnLabels}
                            onCalculationBaseColumnChange={(_channelId, calculationBaseColumn) =>
                                handleGroupCalculationBaseColumnChange(group.id, calculationBaseColumn, formatType.id)
                            }
                            onMilestoneColumnChange={(_channelId, milestoneBaseColumn) =>
                                handleGroupMilestoneColumnChange(group.id, milestoneBaseColumn, formatType.id)
                            }
                        />
                        <div className="channel-milestones">
                            <GroupMilestonesInput
                                hasMilestones={hasMilestones}
                                group={group}
                                milestoneBaseColumn={resolveChannelMilestoneBaseColumn(referenceChannel, override)}
                                onChange={(newGroupData) =>
                                    handleGroupUpdate(newGroupData, group.channels, formatType.id)
                                }
                                propMilestones={override.data as ChannelData[]}
                                useFormData={useFormData}
                            />
                        </div>
                    </div>
                );
            })}
        </div>
    );
};

export default GroupFormatOverrides;
